import * as React from "react";
import s from "./revelacoes.scss";
import { MultiPlaceRetrospectiveData, RetrospectiveData } from "../../api/types";
import Icon from "#components/icon";
import { revelacoesContent } from "./content";
import { replacePlaceholders } from "../../utils/replace-placeholders";
import { PlaceCarousel } from "../place-carousel";
import { Tooltip } from "#components/primitive";

interface RevelacoesProps {
	revelations: RetrospectiveData["revelations"];
	multiPlaceData?: MultiPlaceRetrospectiveData;
	selectedPlaceIndex?: number;
	onPlaceIndexChange?: (index: number) => void;
}

function capitalizeFirstLetter(text: string): string {
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function Revelacoes({
	revelations,
	multiPlaceData,
	selectedPlaceIndex = 0,
	onPlaceIndexChange,
}: RevelacoesProps) {
	const filteredPlaces = React.useMemo(() => {
		if (!multiPlaceData) return [];
		return multiPlaceData.places.filter(place => place.id !== "aggregated");
	}, [multiPlaceData]);

	const adjustedIndex = React.useMemo(() => {
		if (!multiPlaceData) return 0;
		if (selectedPlaceIndex === 0) {
			return 0;
		}
		return selectedPlaceIndex - 1;
	}, [multiPlaceData, selectedPlaceIndex]);

	const handlePlaceIndexChange = React.useCallback(
		(newIndex: number) => {
			if (!multiPlaceData || !onPlaceIndexChange) return;
			onPlaceIndexChange(newIndex + 1);
		},
		[multiPlaceData, onPlaceIndexChange],
	);

	const currentRevelations = React.useMemo(() => {
		if (!multiPlaceData) return revelations;

		const places = multiPlaceData.places;

		const effectiveIndex = selectedPlaceIndex === 0 ? 1 : selectedPlaceIndex;

		if (effectiveIndex < 0 || effectiveIndex >= places.length) {
			console.warn(
				`Revelacoes: effectiveIndex ${effectiveIndex} está fora do range (0-${
					places.length - 1
				})`,
			);
			return revelations;
		}

		const currentPlace = places[effectiveIndex];

		if (!currentPlace) {
			console.warn(`Revelacoes: currentPlace não encontrado no índice ${effectiveIndex}`);
			return revelations;
		}

		const placeRevelations = multiPlaceData.byPlace[currentPlace.id]?.revelations;

		if (!placeRevelations) {
			console.warn(
				`Revelacoes: dados não encontrados para place ${currentPlace.id}`,
				multiPlaceData.byPlace,
			);
		}

		return placeRevelations || revelations;
	}, [multiPlaceData, selectedPlaceIndex, revelations]);

	return (
		<section id="revelacoes" className={s.revelacoes}>
			<div className={s.sectionContainer}>
				<h2 className={s.sectionTitle}>{revelacoesContent.title}</h2>
				{multiPlaceData && filteredPlaces.length > 1 && (
					<p className={s.sectionSubtitle}>{revelacoesContent.subtitle}</p>
				)}

				{multiPlaceData && onPlaceIndexChange && filteredPlaces.length > 1 && (
					<PlaceCarousel
						places={filteredPlaces}
						currentIndex={adjustedIndex}
						onChangeIndex={handlePlaceIndexChange}
					/>
				)}

				<div className={s.pulseCards}>
					<div className={`${s.pulseCard} ${s.pulseOrange}`}>
						<div className={s.pulseIcon}>
							<Icon icon="ri-bar-chart-box-line" />
						</div>
						<div>
							<h4>{revelacoesContent.consumptionPeak.title}</h4>
							<p>
								{replacePlaceholders(revelacoesContent.consumptionPeak.text, {
									dayOfWeek: currentRevelations.dayOfWeek,
									percentageOfSales: currentRevelations.percentageOfSalesDayOfWeek,
								})}
							</p>
						</div>
					</div>
					<div className={`${s.pulseCard} ${s.pulseBlue}`}>
						<div className={s.pulseIcon}>
							<Icon icon="ri-time-line" />
						</div>
						<div>
							<h4>{revelacoesContent.goldenHour.title}</h4>
							<p>
								{replacePlaceholders(revelacoesContent.goldenHour.text, {
									timeRange: currentRevelations.timeRange,
									percentageOfSales: currentRevelations.percentageOfSalesTimeRange,
								})}
							</p>
						</div>
					</div>
					<div className={`${s.pulseCard} ${s.pulsePink}`}>
						<div className={s.pulseIcon}>
							<Icon icon="ri-trophy-line" />
						</div>
						<div>
							<h4>{revelacoesContent.championProduct.title}</h4>
							<Tooltip
								content="Porcentagem em relação ao faturamento Geral"
								placement="top"
							>
								<p>
									{replacePlaceholders(revelacoesContent.championProduct.text, {
										name: capitalizeFirstLetter(currentRevelations.championProduct.name),
										percentage: currentRevelations.championProduct.percentage,
									})}
								</p>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
